// #region IMPORT
import { IK_SELECTORS } from 'components/PoseMode/constants';
import { posingStore } from 'stores';
import { GizmoModeType } from 'stores/posingStore';

import type PosingSystem from '../PosingSystem';
// @ts-expect-error ts(7016) : Could not find a declaration file for module
import { Quat, Vec3 } from '../ossos/ossos.min';
import MachineBase from './MachineBase';

// #endregion

export default class LimbMachine extends MachineBase {
  name = 'limb';
  pSystem!: PosingSystem;
  chainKey!: string;

  // #region STATE MACHINE OVERRIDES
  onInit(ps: PosingSystem) {
    const sel = IK_SELECTORS[posingStore.state.activeIKChain];
    this.chainKey = sel.chainKey;
    this.pSystem = ps;

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Start Gizmo
    posingStore.state.gizmoMode = GizmoModeType.Translate;

    const pos = ps.poser.rig.getTargetPos(this.chainKey, true);
    ps.gizmo.resetTransform(pos, [0, 0, 0, 1]).show();
  }
  // #endregion

  // #region GIZMO EVENT OVERRIDES
  onGizmoDragStart(): void {
    this.pSystem.poser.selectors.clear();
  }

  onGizmoDragEnd(): void {
    this.pSystem.poser.refresh();
  }

  onGizmoTranslate(v: Array<number>): void {
    this.pSystem.poser.rig.setTargetPos(this.chainKey, v);
    this.pSystem.poser.resolve();
  }

  onGizmoRotate(v: Array<number>): void {
    switch (posingStore.state.gizmoMode) {
      // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      case GizmoModeType.Rotate: {
        const rig = this.pSystem.poser.rig;
        const chain = rig[this.chainKey].chain;
        const lnk = chain.links.at(-1);

        const bone = rig.wpose.getBone(lnk.index); // Get final bone in the chain
        const tran = rig.wpose.getWorldTransform(lnk.pindex); // get parent bone's worldspace transform

        tran.rot.invert().mul(v); // convert rotation to local space :: invert( parent.rot ) * gizmo.rot
        bone.local.rot.copy(tran.rot);

        this.pSystem.poser.refreshSkeleton();
        break;
      }

      // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      case GizmoModeType.zTwist: {
        const rig = this.pSystem.poser.rig;
        const ik = rig[this.chainKey];
        const dir = new Vec3().fromQuat(v, [0, 1, 0]);

        ik.target.setPoleDir(dir);
        this.pSystem.poser.resolve();

        break;
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onGizmoModeChange(mode: string): void {
    this.initGizmo();
  }
  // #endregion

  // #region HISTORY EVENT OVERRIDES
  onUndoRedo() {
    this.initGizmo();
  }
  // #endregion

  // #region MISC

  /** Initialize the gizmo's starting position & rotation */
  initGizmo() {
    const rig = this.pSystem.poser.rig;
    const pos = rig.getTargetPos(this.chainKey, true);
    // const Debug = this.pSystem.vdebug;

    switch (posingStore.state.gizmoMode) {
      // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      case GizmoModeType.Rotate: {
        // Get current worldspace rotation of final bone in the chain
        const chain = rig[this.chainKey].chain;
        const lnk = chain.links.at(-1);
        const ctran = rig.wpose.getWorldTransform(lnk.index);

        this.pSystem.gizmo.resetTransform(pos, ctran.rot);
        break;
      }

      // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      case GizmoModeType.zTwist: {
        const chain = rig[this.chainKey].chain;

        const atran = rig.wpose.getWorldTransform(chain.links[0].index);
        const btran = rig.wpose.getWorldTransform(chain.links[2].index);

        const swing = new Vec3().fromSub(btran.pos, atran.pos);
        const twist = new Vec3().fromQuat(atran.rot, chain.links[0].axes.twist);
        const axes = Vec3.orthogonal(swing, twist);
        const rot = new Quat().fromLookDir(axes[2], axes[1]);

        // Debug.pnt.push(atran.pos, 0x00ff00, 1);
        // Debug.pnt.push(btran.pos, 0xff0000, 1);

        // Debug.ln.push(atran.pos, swing.add(atran.pos), 0x00ff00);
        // Debug.ln.push(atran.pos, twist.add(atran.pos), 0xff0000);

        this.pSystem.gizmo.resetTransform(pos, rot);
        break;
      }

      // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      case GizmoModeType.Translate:
        this.pSystem.gizmo.resetTransform(pos);
        break;
    }
  }

  // #endregion
}
